import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const LOGO = '/images/logos/Hairchitech_Logo1.png';
const GOLD = '#7d6633';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" 
        style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}>
      </div>

      {/* Abstract Shapes */}
      <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" 
        style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}>
      </div>
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" 
        style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}>
      </div>
      
      <div className="relative w-full max-w-2xl mx-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: GOLD }}
            >
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
          </div>

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={LOGO} alt="Hairchitech" className="h-16 w-16" />
          </div>

          {/* Heading */}
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4"
          >
            Your subscription is active!
          </motion.h1>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-8"
          >
            <p className="text-lg text-gray-700 mb-4">
              Thank you for subscribing to Hairchitech!
            </p>
            <p className="text-gray-600 mb-2">
              Your subscription has been activated successfully.
            </p>
            <p className="text-sm text-gray-500">
              Check your email for next steps to access your account.
            </p>
          </motion.div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8"
          >
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Next Steps
            </h3>
            <ol className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="font-semibold mr-2">1.</span>
                <span>Check your email for account setup instructions</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">2.</span>
                <span><strong>New users:</strong> Click the link in your email to set your password</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">3.</span>
                <span><strong>Existing users:</strong> Sign in with your existing password</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">4.</span>
                <span>Start using Hairchitech's AI-powered features!</span>
              </li>
            </ol>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/?auth=1')}
              className="px-8 py-4 rounded-xl font-semibold text-white shadow-lg hover:opacity-90 transition-all duration-200"
              style={{ background: GOLD }}
            >
              Go to Sign In
            </button>
            <Link
              to="/"
              className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200 text-center"
            >
              Back to Home
            </Link>
          </motion.div>

          {/* Support Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-gray-500 mb-2">
              <strong>New users:</strong> If you don't receive an email within 5 minutes, check your spam folder.
            </p>
            <p className="text-sm text-gray-500">
              <strong>Existing users:</strong> Your subscription has been updated. Sign in with your existing credentials.{' '}
              <a 
                href="mailto:support@hairchitech.com" 
                className="font-medium hover:opacity-80 transition-opacity"
                style={{ color: GOLD }}
              >
                Need help?
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;

